import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const PREFIX = "/downloads/partners/";
const BUCKET = "partner-resources";

type State = "loading" | "allowed" | "anon" | "pending";

/**
 * Protects partner resource pages: only signed-in approved partners (or staff)
 * can view them. Links to /downloads/partners/* are served from private storage
 * through short-lived signed URLs.
 */
export default function PartnerGuard({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>("loading");
  const location = useLocation();

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return active && setState("anon");
      const [{ data: profile }, { data: roles }] = await Promise.all([
        supabase.from("profiles").select("status").eq("id", user.id).maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", user.id),
      ]);
      const staff = (roles ?? []).some((r) => r.role === "admin" || r.role === "account_manager");
      if (active) setState(staff || profile?.status === "approved" ? "allowed" : "pending");
    })();
    return () => { active = false; };
  }, []);

  usePartnerDownloads(state === "allowed");

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Cargando…</div>;
  }
  if (state === "anon") return <Navigate to="/partners/login" replace state={{ from: location.pathname }} />;
  if (state === "pending") return <Navigate to="/partners/portal" replace />;
  return <>{children}</>;
}

/** Serves /downloads/partners/* links from private storage via signed URLs. */
export function usePartnerDownloads(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const onClick = async (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest?.("a") as HTMLAnchorElement | null;
      const href = a?.getAttribute("href");
      if (!href || !href.startsWith(PREFIX)) return;
      e.preventDefault();
      const path = decodeURIComponent(href.slice(PREFIX.length));
      const win = window.open("", "_blank");
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 300, { download: path });
      if (error || !data?.signedUrl) {
        win?.close();
        toast.error("No fue posible descargar el archivo. Intenta de nuevo.");
        return;
      }
      if (win) win.location.href = data.signedUrl;
      else window.location.href = data.signedUrl;
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [enabled]);

}
